import { ArrowDown, ArrowUp, FileText, Link2, Plus, Trash2 } from "lucide-react";
import { fmtDuration } from "@/lib/time";
import { blankSub, blankTask, subTotal, taskTotal } from "@/lib/tasks";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const small = "rounded-lg bg-white border-[#103080]/20 h-9 text-sm";

function Pick({ value, onChange, options, none, width = "w-40", testId }) {
  const opts = value && !options.some((o) => o.value === value) ? [{ value, label: value }, ...options] : options;
  return (
    <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
      <SelectTrigger className={`${small} ${width}`} data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>
        {none && <SelectItem value="none">{none}</SelectItem>}
        {opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function SubtaskRow({ s, set, remove, testId }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[minmax(0,2fr)_80px_minmax(0,1fr)_minmax(0,1fr)_24px] gap-2 items-center" data-testid={testId}>
      <Input required value={s.title} onChange={(e) => set({ title: e.target.value })} placeholder="Subtask" className={small} data-testid={`${testId}-title`} />
      <Input type="number" min={0} step={5} value={s.duration_minutes} onChange={(e) => set({ duration_minutes: e.target.value })} className={small} aria-label="Minutes" data-testid={`${testId}-minutes`} />
      <div className="relative"><FileText size={13} className="absolute left-2.5 top-3 text-[#0090D0]" />
        <Input value={s.sop_link} onChange={(e) => set({ sop_link: e.target.value })} placeholder="SOP link" className={`${small} pl-7`} data-testid={`${testId}-sop`} /></div>
      <div className="relative"><Link2 size={13} className="absolute left-2.5 top-3 text-[#0090D0]" />
        <Input value={s.template_link} onChange={(e) => set({ template_link: e.target.value })} placeholder="Email / template link" className={`${small} pl-7`} data-testid={`${testId}-link`} /></div>
      <button type="button" onClick={remove} aria-label="Remove subtask" className="text-[#103080]/40 hover:text-red-600 transition-colors duration-200" data-testid={`${testId}-remove`}><Trash2 size={13} /></button>
    </div>
  );
}

function TaskBlock({ t, i, count, set, remove, move, roleNames, statuses, directory, testPrefix }) {
  const id = `${testPrefix}-task-${i}`;
  const subs = t.subtasks || [];
  const setSub = (j, patch) => set({ subtasks: subs.map((s, k) => (k === j ? { ...s, ...patch } : s)) });
  return (
    <div className="rounded-xl border border-[#103080]/10 bg-white p-3 space-y-2.5" data-testid={id}>
      <div className="flex flex-wrap items-center gap-2">
        <Input required value={t.title} onChange={(e) => set({ title: e.target.value })} placeholder="Task, e.g. Prepare accounts" className={`${small} font-semibold flex-1 min-w-[200px]`} data-testid={`${id}-title`} />
        <Pick value={t.role} onChange={(v) => set({ role: v })} none="No role" options={roleNames.map((n) => ({ value: n, label: n }))} testId={`${id}-role`} />
        {directory && <Pick value={t.assignee_id} onChange={(v) => set({ assignee_id: v })} options={directory.map((p) => ({ value: p.id, label: p.name }))} width="w-36" testId={`${id}-assignee`} />}
        <Pick value={t.sets_status} onChange={(v) => set({ sets_status: v })} none="No status change" options={statuses.map((s) => ({ value: s, label: `When done: ${s}` }))} width="w-56" testId={`${id}-sets-status`} />
        {subTotal(subs) > 0 ? <span className="text-xs font-semibold text-[#0090D0] w-16 text-right" data-testid={`${id}-total`}>{fmtDuration(taskTotal(t))}</span> : (
          <Input type="number" min={5} step={5} value={t.duration_minutes} onChange={(e) => set({ duration_minutes: e.target.value })} className={`${small} w-20`} aria-label="Minutes" data-testid={`${id}-minutes`} />
        )}
        <div className="flex items-center gap-1 text-[#103080]/40">
          <button type="button" disabled={i === 0} onClick={() => move(-1)} aria-label="Move up" className="hover:text-[#103080] disabled:opacity-30"><ArrowUp size={14} /></button>
          <button type="button" disabled={i === count - 1} onClick={() => move(1)} aria-label="Move down" className="hover:text-[#103080] disabled:opacity-30"><ArrowDown size={14} /></button>
          <button type="button" onClick={remove} aria-label="Remove task" className="text-red-500 hover:text-red-700 ml-1" data-testid={`${id}-remove`}><Trash2 size={15} /></button>
        </div>
      </div>
      <div className="pl-3 border-l-2 border-[#0090D0]/20 space-y-2">
        {subs.map((s, j) => <SubtaskRow key={j} s={s} set={(p) => setSub(j, p)} remove={() => set({ subtasks: subs.filter((_, k) => k !== j) })} testId={`${id}-sub-${j}`} />)}
        <button type="button" onClick={() => set({ subtasks: [...subs, blankSub()] })} data-testid={`${id}-add-sub`}
          className="text-xs font-semibold text-[#0090D0] inline-flex items-center gap-1 hover:text-[#103080] transition-colors duration-200"><Plus size={12} /> Add subtask</button>
      </div>
    </div>
  );
}

export default function TaskBuilder({ tasks, onChange, roleNames = [], statuses = [], directory, defaultAssignee, testPrefix }) {
  const set = (i, patch) => onChange(tasks.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  const move = (i, d) => {
    const next = [...tasks];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    onChange(next);
  };
  return (
    <div className="space-y-3" data-testid={`${testPrefix}-builder`}>
      {tasks.map((t, i) => (
        <TaskBlock key={i} t={t} i={i} count={tasks.length} set={(p) => set(i, p)} remove={() => onChange(tasks.filter((_, j) => j !== i))} move={(d) => move(i, d)}
          roleNames={roleNames} statuses={statuses} directory={directory} testPrefix={testPrefix} />
      ))}
      <button type="button" onClick={() => onChange([...tasks, blankTask(directory ? defaultAssignee : undefined)])} data-testid={`${testPrefix}-add-task`}
        className="inline-flex items-center gap-1.5 rounded-full border border-[#103080]/20 px-4 h-10 text-sm font-medium text-[#103080] hover:border-[#0090D0] transition-colors duration-200"><Plus size={14} /> Add task</button>
    </div>
  );
}
